import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

/**
 * Counts how many times OTHER users have saved/favorited
 * listings owned by the current user (across all 5 asset types).
 */
export const useListingSaves = () => {
  const { user } = useAuth();
  const [savesCount, setSavesCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setSavesCount(0);
      return;
    }

    const fetchSaves = async () => {
      try {
        // Get all listing IDs owned by the current user across all 5 tables
        const [heliRes, engineRes, partsRes, jobsRes, servicesRes] = await Promise.all([
          supabase.from('helicopter_listings').select('id').eq('user_id', user.id),
          supabase.from('engine_listings').select('id').eq('user_id', user.id),
          supabase.from('parts_listings').select('id').eq('user_id', user.id),
          supabase.from('job_listings').select('id').eq('user_id', user.id),
          supabase.from('service_listings').select('id').eq('user_id', user.id),
        ]);

        const allIds = [
          ...(heliRes.data?.map(r => r.id) || []),
          ...(engineRes.data?.map(r => r.id) || []),
          ...(partsRes.data?.map(r => r.id) || []),
          ...(jobsRes.data?.map(r => r.id) || []),
          ...(servicesRes.data?.map(r => r.id) || []),
        ];

        if (allIds.length === 0) {
          setSavesCount(0);
          setLoading(false);
          return;
        }

        // Count favorites by OTHER users on these listings
        const { count, error } = await supabase
          .from('user_favorites')
          .select('*', { count: 'exact', head: true })
          .in('listing_id', allIds)
          .neq('user_id', user.id);

        if (error) throw error;
        setSavesCount(count || 0);
      } catch (err) {
        console.error('Error fetching listing saves:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSaves();

    // Listen for changes to user_favorites
    const channel = supabase
      .channel('listing-saves-count')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_favorites' },
        () => { fetchSaves(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  return { savesCount, loading };
};
